import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { atom } from "jotai"
import { useAtom } from "jotai"
import svgRadialGradient from "../assets/radial-gradient.svg"
import { ContainerShape, HexagonShape, InnerShape } from "./Shape"
import { useResolution } from "../hooks/useResolution"

type Notification = {
  id: string
  title: string
  description: string
  image: string
}

let notificationId = 0

const atomNotification = atom<Notification[]>([])

export const useNotification = () => {
  const [items, setItems] = useAtom(atomNotification)

  const addNotification = (notification: Omit<Notification, 'id'>) => {
    const newNotification = { ...notification, id: `notification-${notificationId++}` }
    setItems(prev => [...prev, newNotification])
  }

  const removeNotification = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id))
  }

  return {
    items,
    addNotification,
    removeNotification,
  }
}

export const NotificationHandler = () => {
  const { items, removeNotification } = useNotification()
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null)
  const [animationComplete, setAnimationComplete] = useState(0) // 강제 리렌더링용
  const isAnimatingRef = useRef(false)

  // 다음 토스트 표시
  useEffect(() => {
    if (!currentNotification && !isAnimatingRef.current && items.length > 0) {
      const nextNotification = items[0]
      setCurrentNotification(nextNotification)
      isAnimatingRef.current = true
    }
  }, [currentNotification, items, animationComplete])

  // 현재 토스트 자동 제거
  useEffect(() => {
    if (currentNotification) {
      const timer = setTimeout(() => {
        handleRemoveNotification(currentNotification.id)
      }, 2500)

      return () => {
        clearTimeout(timer)
      }
    }
  }, [currentNotification])

  const handleRemoveNotification = (id: string) => {
    removeNotification(id)
    setCurrentNotification(null)
    
    // 애니메이션 완료 후 다음 토스트 준비
    setTimeout(() => {
      isAnimatingRef.current = false
      setAnimationComplete(prev => prev + 1) // 다음 토스트 체크 강제 실행
    }, 500) // exit 애니메이션 시간을 고려
  }

  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <AnimatePresence>
        {currentNotification && (
          <Notification key={currentNotification.id} {...currentNotification} />
        )}
      </AnimatePresence>
    </div>
  )
}

const Notification = ({ id, title, description, image }: Notification) => {
  const { isMobile } = useResolution()
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowDetails(true)
    }, 1200)

    return () => clearTimeout(timer)
  }, [])

  return (
    <div className='absolute left-1/2 bottom-10 -translate-x-1/2'>
      <motion.div 
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
      >
        <div
          className="relative"
          style={{
            // filter: 'drop-shadow(0 0px 20px rgb(0 0 0 / 50%))'
          }}
        >
          <div className='absolute left-8 top-8 -translate-x-1/2 -translate-y-1/2 aspect-square w-[150px] opacity-50'>
            <motion.img 
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: [1, 1, 1, 1, 0], scale: 1 }}
              src={svgRadialGradient}
              className="w-full h-full"
            />
          </div>

          <ContainerShape cornerDiagonal={20} cornerRadius={12} strokeWidth={2}>
            <motion.div 
              initial={{ width: 68 }}
              animate={{ width: isMobile ? 340 : 380 }}
              transition={{ delay: 0.5 }}
              className="flex items-center gap-4 p-2.5 font-blinko"
            >
              <div className='relative z-10'>
                <InnerShape cornerDiagonal={14} cornerRadius={6} strokeWidth={2}>
                  <div className='w-12 h-12 p-1.5'>
                    <motion.img 
                      src={image} 
                      alt={title}
                      className="h-full object-contain"
                    />
                  </div>
                </InnerShape>
              </div>

              <div className='relative'>
                <div className="absolute inset-0">
                  <motion.p
                    className='text-white whitespace-nowrap'
                    initial={{
                      x: -20, 
                      y: -13,
                      opacity: 0,

                      fontSize: '20px',
                      fontWeight: 700
                    }}
                    animate={
                      !showDetails ? { 
                        x: 0, 
                        opacity: 1,

                        transition: {
                          delay: 0.6
                        }
                      } : {
                        x: 0,
                        opacity: 0.5,
                        y: -17,

                        fontSize: '12px',
                        fontWeight: 500,
                      }
                    }
                  >
                    {title}
                  </motion.p>
                </div>
                
                <div className="absolute inset-0">
                  {showDetails && (
                    <motion.p
                      layoutId={`${id}-description`}
                      layout
                      className='text-[20px] font-bold text-white whitespace-nowrap'
                      initial={{ x: -20, y: -2, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      {description}
                    </motion.p>
                  )}
                </div>
              </div>
            </motion.div>
          </ContainerShape>
        </div>
      </motion.div>
    </div>
  )
}